import { getDevFlag } from '../../constants/devFlags';

export const HIRING_DEPTHS = {
  MARKET: 'market',
  COMPANY: 'company',
  VENDOR: 'vendor',
  CATEGORY: 'category',
};

export const HIRING_VIEWS = {
  SUMMARY: 'summary',
  TREND: 'trend',
  JOB_BOARD: 'jobBoard',
  ROLES: 'roles',
  LEADERSHIP: 'leadership',
  DAILY: 'daily',
  PARTNER_TRENDS: 'partner-trends',
  PARTNER_JOBS: 'partner-jobs',
};

export const SHOW_DAILY_HIRING = getDevFlag('JOB_POSTINGS_DAILY');

export const HIRING_DEPTH_STORAGE_KEY = 'jobPostingsDepth';
export const HIRING_DEPTH_VIEW_STORAGE_KEY = 'jobPostingsDepthView';
export const HIRING_LEGACY_VIEW_STORAGE_KEY = 'jobPostingsView';
export const HIRING_VIEW_EVENT_NAME = 'jobsViewChange';
export const HIRING_NAV_EVENT_NAME = 'jobsNavChange';

export const DEFAULT_HIRING_NAV = {
  depth: HIRING_DEPTHS.VENDOR,
  view: HIRING_VIEWS.SUMMARY,
};

export const HIRING_VIEW_OPTIONS_BY_DEPTH = {
  [HIRING_DEPTHS.COMPANY]: [
    HIRING_VIEWS.SUMMARY,
    HIRING_VIEWS.TREND,
    HIRING_VIEWS.JOB_BOARD,
    HIRING_VIEWS.ROLES,
    HIRING_VIEWS.LEADERSHIP,
  ],
  [HIRING_DEPTHS.VENDOR]: [
    HIRING_VIEWS.SUMMARY,
    HIRING_VIEWS.TREND,
    HIRING_VIEWS.ROLES,
  ],
  [HIRING_DEPTHS.CATEGORY]: [
    HIRING_VIEWS.SUMMARY,
    HIRING_VIEWS.TREND,
    HIRING_VIEWS.ROLES,
    HIRING_VIEWS.LEADERSHIP,
  ],
};

export const DEFAULT_HIRING_LAB_NAV = {
  depth: HIRING_DEPTHS.VENDOR,
  view: HIRING_VIEWS.TREND,
};

export const HIRING_LAB_VIEW_OPTIONS_BY_DEPTH = {
  [HIRING_DEPTHS.COMPANY]: [
    HIRING_VIEWS.TREND,
    HIRING_VIEWS.JOB_BOARD,
    ...(SHOW_DAILY_HIRING ? [HIRING_VIEWS.DAILY] : []),
    HIRING_VIEWS.PARTNER_TRENDS,
  ],
  [HIRING_DEPTHS.VENDOR]: [
    HIRING_VIEWS.TREND,
    HIRING_VIEWS.ROLES,
    ...(SHOW_DAILY_HIRING ? [HIRING_VIEWS.DAILY] : []),
    HIRING_VIEWS.PARTNER_TRENDS,
  ],
  [HIRING_DEPTHS.CATEGORY]: [
    HIRING_VIEWS.TREND,
    HIRING_VIEWS.LEADERSHIP,
    HIRING_VIEWS.PARTNER_TRENDS,
  ],
};

export const HIRING_WORKSPACE_VIEW_OPTIONS_BY_DEPTH = HIRING_LAB_VIEW_OPTIONS_BY_DEPTH;

export const LEGACY_HIRING_VIEW_MAP = {
  dashboard: { depth: HIRING_DEPTHS.VENDOR, view: HIRING_VIEWS.SUMMARY },
  trends: { depth: HIRING_DEPTHS.CATEGORY, view: HIRING_VIEWS.SUMMARY },
  market: { depth: HIRING_DEPTHS.CATEGORY, view: HIRING_VIEWS.SUMMARY },
  company: { depth: HIRING_DEPTHS.COMPANY, view: HIRING_VIEWS.SUMMARY },
  vendor: { depth: HIRING_DEPTHS.VENDOR, view: HIRING_VIEWS.SUMMARY },
  category: { depth: HIRING_DEPTHS.CATEGORY, view: HIRING_VIEWS.SUMMARY },
  executives: { depth: HIRING_DEPTHS.CATEGORY, view: HIRING_VIEWS.LEADERSHIP },
  rolemix: { depth: HIRING_DEPTHS.VENDOR, view: HIRING_VIEWS.ROLES },
};

export const HIRING_LEGACY_VIEW_BY_NAV = {
  [`${HIRING_DEPTHS.COMPANY}:${HIRING_VIEWS.SUMMARY}`]: 'company',
  [`${HIRING_DEPTHS.COMPANY}:${HIRING_VIEWS.TREND}`]: 'company',
  [`${HIRING_DEPTHS.COMPANY}:${HIRING_VIEWS.JOB_BOARD}`]: 'company',
  [`${HIRING_DEPTHS.COMPANY}:${HIRING_VIEWS.ROLES}`]: 'company',
  [`${HIRING_DEPTHS.COMPANY}:${HIRING_VIEWS.LEADERSHIP}`]: 'company',
  [`${HIRING_DEPTHS.VENDOR}:${HIRING_VIEWS.SUMMARY}`]: 'vendor',
  [`${HIRING_DEPTHS.VENDOR}:${HIRING_VIEWS.TREND}`]: 'vendor',
  [`${HIRING_DEPTHS.VENDOR}:${HIRING_VIEWS.ROLES}`]: 'rolemix',
  [`${HIRING_DEPTHS.CATEGORY}:${HIRING_VIEWS.SUMMARY}`]: 'category',
  [`${HIRING_DEPTHS.CATEGORY}:${HIRING_VIEWS.TREND}`]: 'category',
  [`${HIRING_DEPTHS.CATEGORY}:${HIRING_VIEWS.ROLES}`]: 'rolemix',
  [`${HIRING_DEPTHS.CATEGORY}:${HIRING_VIEWS.LEADERSHIP}`]: 'executives',
};

export const HIRING_RENDER_VIEWS = {
  COMPANY: 'company',
  VENDOR: 'vendor',
  CATEGORY: 'category',
  ROLE_MIX: 'rolemix',
  LEADERSHIP: 'executives',
  DAILY: 'daily',
};

export const isValidHiringDepth = (depth) => (
  Object.values(HIRING_DEPTHS).includes(depth)
);

export const isValidHiringViewForDepth = (depth, view) => (
  Boolean(HIRING_VIEW_OPTIONS_BY_DEPTH[depth]?.includes(view))
);

export const normalizeHiringNav = ({
  depth,
  view,
  legacyView,
} = {}) => {
  if (legacyView && LEGACY_HIRING_VIEW_MAP[legacyView]) {
    return LEGACY_HIRING_VIEW_MAP[legacyView];
  }

  const normalizedDepth = isValidHiringDepth(depth)
    ? depth
    : DEFAULT_HIRING_NAV.depth;
  const normalizedView = isValidHiringViewForDepth(normalizedDepth, view)
    ? view
    : DEFAULT_HIRING_NAV.view;

  return {
    depth: normalizedDepth,
    view: normalizedView,
  };
};

export const isValidHiringLabViewForDepth = (depth, view) => (
  Boolean(HIRING_LAB_VIEW_OPTIONS_BY_DEPTH[depth]?.includes(view))
);

export const normalizeHiringLabNav = ({
  depth,
  view,
} = {}) => {
  const normalizedDepth = isValidHiringDepth(depth)
    ? depth
    : DEFAULT_HIRING_LAB_NAV.depth;
  const requestedView = normalizedDepth === HIRING_DEPTHS.COMPANY && view === HIRING_VIEWS.PARTNER_JOBS
    ? HIRING_VIEWS.PARTNER_TRENDS
    : view;
  const normalizedView = isValidHiringLabViewForDepth(normalizedDepth, requestedView)
    ? requestedView
    : HIRING_LAB_VIEW_OPTIONS_BY_DEPTH[normalizedDepth]?.[0] || DEFAULT_HIRING_LAB_NAV.view;

  return {
    depth: normalizedDepth,
    view: normalizedView,
  };
};

export const normalizeHiringWorkspaceNav = normalizeHiringLabNav;

export const normalizeHiringLabRoute = ({
  pathDepth,
  pathView,
} = {}) => {
  return {
    nav: normalizeHiringLabNav({
      depth: pathDepth,
      view: pathView,
    }),
  };
};

export const normalizeHiringWorkspaceRoute = normalizeHiringLabRoute;

export const getLegacyHiringView = (depth, view) => (
  HIRING_LEGACY_VIEW_BY_NAV[`${depth}:${view}`] || HIRING_LEGACY_VIEW_BY_NAV[`${depth}:${HIRING_VIEWS.SUMMARY}`] || 'vendor'
);

export const getHiringRenderView = (depth, view) => {
  if (view === HIRING_VIEWS.DAILY) return HIRING_RENDER_VIEWS.DAILY;
  if (view === HIRING_VIEWS.ROLES) return HIRING_RENDER_VIEWS.ROLE_MIX;
  if (view === HIRING_VIEWS.LEADERSHIP) return HIRING_RENDER_VIEWS.LEADERSHIP;
  if (depth === HIRING_DEPTHS.COMPANY) return HIRING_RENDER_VIEWS.COMPANY;
  if (depth === HIRING_DEPTHS.CATEGORY) return HIRING_RENDER_VIEWS.CATEGORY;
  return HIRING_RENDER_VIEWS.VENDOR;
};

export const isPartnerHiringView = (view) => (
  view === HIRING_VIEWS.PARTNER_TRENDS || view === HIRING_VIEWS.PARTNER_JOBS
);

export const getSIHiringViewFromHiringView = (depth, view) => (
  depth === HIRING_DEPTHS.COMPANY
    ? 'dashboard'
    : 'trends'
);

export const buildHiringLabPath = ({
  depth,
  view,
  ticker,
} = {}) => {
  const nav = normalizeHiringLabNav({ depth, view });
  const base = `/hiring/${nav.depth}/${nav.view}`;
  const normalizedTicker = String(ticker || '').trim().toUpperCase();
  if (nav.depth !== HIRING_DEPTHS.COMPANY || !normalizedTicker) return base;
  const params = new URLSearchParams();
  params.set('ticker', normalizedTicker);
  return `${base}?${params.toString()}`;
};

export const buildHiringWorkspacePath = buildHiringLabPath;
